import { useState } from 'react';
import { Dialog, DialogContent, Button, Alert } from '@mui/material';
import EditLocation from './EditLocation';
import DialogActionBar from './DialogActionBar';
import { validateLocationData, isLocationDataValid } from '../../../utils/operationValidation';
import { CreateOperationData, Operation, OperationLocation } from '@networkRequest/operationsService';
import { useUser } from '../../../hooks/useUser';
import { updateOperation } from '@networkRequest/operationsService';

interface EditLocationModalProps {
  open: boolean;
  onClose: () => void;
  primaryLocation: OperationLocation;
  operation: Operation;
  onOperationUpdate: (op: Operation) => void;
}

export default function EditLocationModal({
  open,
  onClose,
  primaryLocation,
  operation,
  onOperationUpdate,
}: EditLocationModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<OperationLocation>(primaryLocation);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUser();

  const handleSave = async () => {
    if (!user?.accessToken || !operation?.id) {
      setError('Authentication required or operation not found');
      return;
    }

    const validationErrors = validateLocationData(formData);

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      const locationsWithoutPrimary = operation.operation_locations.filter(location => !location.primary)

      const updateData: Partial<CreateOperationData> = {
        operation_locations_attributes: [...locationsWithoutPrimary, formData]
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.data) {
        onOperationUpdate(result.data);
        onClose();
        setError(null);
      }

    } catch (error) {
      setError('Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '900px',
          maxWidth: '900px',
          height: 'auto',
          maxHeight: '90vh',
          pt: 4,
          px: 3,
          pb: 2
        },
      }}
    >
      <DialogContent sx={{ p: 0, mb: 3 }}>
        <EditLocation
          title="Edit Primary Location"
          locationData={formData}
          onLocationDataChange={(field: string, value: string) => {
            setFormData((prev) => ({
              ...prev,
              [field]: value
            }));
          }}
          backgroundColor='common/white_states/main'
        />
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActionBar sx={{ paddingRight: 4 }}>
        <Button onClick={onClose} variant="outlined" size="medium">
          Cancel
        </Button>
        <Button
          onClick={() => handleSave()}
          variant="contained"
          size="medium"
          disabled={loading || !isLocationDataValid(formData)}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActionBar>
    </Dialog>
  );
}